import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { money, formatError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Building2, Sparkles, Percent, Gift, Check, ShieldCheck, Loader2 } from "lucide-react";

export default function TradePortal() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [applying, setApplying] = useState(false);

  const apply = async () => {
    if (!user) { navigate("/login"); return; }
    setApplying(true);
    try {
      await api.post("/trade/apply");
      await refresh();
      toast.success("Trade application submitted — our team will verify you shortly.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setApplying(false); }
  };

  const verified = user && user.trade_verified;
  const pending = user && user.buyer_type === "designer" && !verified;

  return (
    <div>
      <Navbar />
      <div className="relative overflow-hidden bg-[var(--bg-dark)] text-white grain">
        <img src="https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <div className="overline-badge">Trade & Designer Program</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-tight">Art for hotels, venues & the designers who curate them</h1>
            <p className="text-stone-300 mt-5 text-lg">
              Dedicated trade support and preferential pricing for interior designers, hospitality groups
              and business buyers furnishing their spaces at scale.
            </p>
            <button onClick={apply} disabled={applying || verified || pending} data-testid="trade-apply-btn"
              className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold mt-8 disabled:opacity-60">
              {applying && <Loader2 className="w-4 h-4 animate-spin" />}
              {verified ? "You're a verified trade member" : pending ? "Application under review" : "Apply for trade access"}
            </button>
          </div>
        </div>
      </div>

      {verified && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-10">
          <div className="bg-white rounded-2xl border border-[var(--gold)] shadow-xl p-8 grid sm:grid-cols-3 gap-6" data-testid="trade-benefits-card">
            <div>
              <div className="overline-badge flex items-center gap-1"><Percent className="w-3.5 h-3.5" /> Your discount</div>
              <div className="font-serif text-4xl mt-2">{user.rebate_discount_pct || 0}%</div>
              <p className="text-xs text-[var(--text-muted)] mt-1">Applied on eligible purchases</p>
            </div>
            <div>
              <div className="overline-badge flex items-center gap-1"><Gift className="w-3.5 h-3.5" /> Art credit</div>
              <div className="font-serif text-4xl mt-2">{money(user.rebate_credit || 0)}</div>
              <p className="text-xs text-[var(--text-muted)] mt-1">Spend toward free art</p>
            </div>
            <div className="flex items-center">
              <Link to="/gallery" className="btn-gold w-full text-center rounded-full py-3 text-sm font-semibold">Shop with rebate</Link>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-3 gap-8">
        {[
          [Building2, "For hospitality & business", "Furnish hotels, gyms, restaurants and venues with curated original art — at volume, with dedicated support and delivery protection on every piece."],
          [Sparkles, "For interior designers", "Specify original art for client projects with dedicated trade support and preferential pricing."],
          [ShieldCheck, "Always protected", "Every trade order is delivery-protected. The artist is paid only once the artwork is delivered and confirmed on site."],
        ].map(([Icon, t, d]) => (
          <div key={t} className="bg-white rounded-2xl p-8 border border-[var(--border-subtle)]">
            <div className="w-12 h-12 rounded-full bg-[var(--gold-light)] flex items-center justify-center text-[var(--gold-hover)]"><Icon className="w-5 h-5" /></div>
            <h3 className="font-serif text-2xl mt-5">{t}</h3>
            <p className="text-sm text-[var(--text-secondary)] mt-3 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>

      <div className="bg-[var(--bg-secondary)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-serif text-3xl text-center">How the rebate works</h2>
          <div className="grid sm:grid-cols-2 gap-6 mt-10">
            {[
              ["Apply & get verified", "Register as an interior designer or business buyer and apply for trade access."],
              ["Receive your rebate tier", "Our team grants you a discount percentage and/or an art credit balance."],
              ["Shop the full gallery", "At checkout, toggle your rebate to instantly reduce the price — down to free."],
              ["Delivery-protected", "Your purchase is delivered with full protection — the artist is paid only on confirmed delivery."],
            ].map(([t, d], i) => (
              <div key={t} className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-[var(--gold)] text-white flex items-center justify-center text-sm font-mono shrink-0">{i + 1}</div>
                <div><h4 className="font-medium">{t}</h4><p className="text-sm text-[var(--text-secondary)] mt-1">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
